// src/components/ExportData.tsx
import React, { useState, useEffect, useRef } from 'react';
import { getExportData } from '../utils/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSettings } from '../context/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { ExportItem } from "../utils/types";
import { displayPrice } from '../utils/general';
import { format } from "date-fns";

interface HomeProps {
    setDisableOverflow: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExportData: React.FC<HomeProps> = ({ setDisableOverflow }) => {

    useEffect(() => {
        // Disable overflow for this page
        setDisableOverflow(true);

        // Optionally, you can reset overflow state when leaving the page
        return () => setDisableOverflow(false); // Optional cleanup when leaving page
    }, [setDisableOverflow]);

    const { settings } = useSettings();
    const navigate = useNavigate();

    const formatDateTimeLocal = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`; // Local time in 'YYYY-MM-DDTHH:MM' format
    };

    useEffect(() => {
        const yesterdayMidnight = new Date();
        yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);
        yesterdayMidnight.setHours(0, 0, 0, 0); // Set to 00:00 local time

        const todayEndTime = new Date();
        todayEndTime.setHours(23, 59, 0, 0); // Set to 23:59 local time

        setStartDatetime(formatDateTimeLocal(yesterdayMidnight));
        setEndDatetime(formatDateTimeLocal(todayEndTime));
    }, []);

    const [startDatetime, setStartDatetime] = useState('');
    const [endDatetime, setEndDatetime] = useState('');
    const [exportData, setExportData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);

    const handleGetExportData = async () => {

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const vendorId = userData.vendor_id; // Get vendor ID from user data
        const token = localStorage.getItem('token');

        if (!vendorId || !token) {
            alert("Vendor ID or token is missing");
            return;
        }

        setLoading(true);
        try {
            const data = await getExportData(vendorId, token, startDatetime, endDatetime);
            setExportData(data);
        } catch (error) {
            console.error("Failed to get export data", error);
            alert("Error retrieving export data");
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false, // 24-hour format
        };

        return date
            .toLocaleString('en-GB', options)
            .replace(',', ''); // Removes the comma after the date
    };


    const calculateDateRange = (type: string) => {
        const today = new Date();
        let startDate = new Date();
        let endDate = new Date();

        switch (type) {
            case 'today':
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'yesterday':
                startDate.setDate(today.getDate() - 1);
                startDate.setHours(0, 0, 0, 0);
                endDate.setDate(today.getDate() - 1);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'thisWeek':
                const firstDayOfWeek = today.getDate() - today.getDay();
                startDate.setDate(firstDayOfWeek);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'lastWeek':
                const firstDayLastWeek = today.getDate() - today.getDay() - 7;
                const lastDayLastWeek = firstDayLastWeek + 6;
                startDate.setDate(firstDayLastWeek);
                startDate.setHours(0, 0, 0, 0);
                endDate.setDate(lastDayLastWeek);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'thisMonth':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
                break;
            case 'lastMonth':
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
                break;
            default:
                break;
        }

        setStartDatetime(formatDateTimeLocal(startDate));
        setEndDatetime(formatDateTimeLocal(endDate));

        handleGetExportData();
    };

    const handleGetExportDataToday = () => calculateDateRange('today');
    const handleGetExportDataYesterday = () => calculateDateRange('yesterday');
    const handleGetExportDataThisWeek = () => calculateDateRange('thisWeek');
    const handleGetExportDataLastWeek = () => calculateDateRange('lastWeek');
    const handleGetExportDataThisMonth = () => calculateDateRange('thisMonth');
    const handleGetExportDataLastMonth = () => calculateDateRange('lastMonth');


    // Generate CSV
    const handleGenerateCSV = () => {
        if (!exportData || exportData.length === 0) {
            alert("No data available to export.");
            return;
        }

        const csvHeaders = ['Order ID', 'Order Date', 'Status', 'Item Name', 'Price', 'Quantity', 'Total Amount', 'Add-on Total'];
        const csvRows: string[][] = exportData.map((item: ExportItem) => [
            item.orderId.toString(), // Convert to string to ensure consistency
            formatDateTime(item.orderDate),
            item.status,
            item.itemName,
            displayPrice(item.price).toString(),
            item.quantity.toString(),
            displayPrice(item.totalAmount).toString(),
            item.addonTotal.toString(),
        ]);

        const csvContent = [
            csvHeaders.join(','), // Add headers
            ...csvRows.map((row: string[]) => row.join(',')) // Add data rows
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Export_Data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const handlePrint = () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
        });

        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('Export Report', 40, 40);

        doc.setFontSize(12);
        doc.setFont('Helvetica', 'normal');

        // Generate the table
        (doc as any).autoTable({
            head: [['Order ID', 'Order Date', 'Status', 'Item Name', 'Price', 'Quantity', 'Total Amount', 'Add-on Total']],
            body: exportData.map((item: ExportItem) => [
                item.orderId,
                formatDateTime(item.orderDate),
                item.status,
                item.itemName,
                `${settings?.currencyName}${displayPrice(item.price)}`,
                item.quantity,
                `${settings?.currencyName}${displayPrice(item.totalAmount)}`,
                `${settings?.currencyName}${item.addonTotal}`,
            ]),
            startY: 60,
            styles: { overflow: 'linebreak', cellPadding: 8, fontSize: 10 },
            margin: { top: 60 },
            theme: 'grid',
        });

        doc.save('Export_Report.pdf');
    };


    function formatDateHumanReadable(dateTime: string) {
        const date = new Date(dateTime);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const year = String(date.getFullYear()).slice(-2);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}-${month}-${year} ${hours}:${minutes}`;
    }

    return (
        <div className='flex flex-col '>
            <div className='flex flex-row justify-between '>
                <h2 className='pageTitle'>Data Export</h2>
                
                <button onClick={() => navigate('/report')} disabled={loading} className='ActionBtn bg-green-500'>
                    {loading ? "Loading..." : "Daily Report"}
                </button>
            </div>
            <div className='flex flex-col md:flex-row items-center justify-center w-screen p-2 '>
                <div>
                    <label>Start Datetime:</label>
                    <input
                        type="datetime-local"
                        value={startDatetime}
                        onChange={(e) => setStartDatetime(e.target.value)}
                        className='border p-2'
                    />
                </div>
                <div>
                    <label>End Datetime:</label>
                    <input
                        type="datetime-local"
                        value={endDatetime}
                        onChange={(e) => setEndDatetime(e.target.value)}
                        className='border p-2'

                    />
                </div>
            </div>

            <div className='flex flex-row items-center justify-center w-screen p-2  shadow-xl'>
                <div>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataToday}>Today</button>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataYesterday}>Yesterday</button>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataThisWeek}>This Week</button>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataLastWeek}>Last Week</button>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataThisMonth}>This Month</button>
                    <button  disabled={loading} className='ActionBtn bg-blue-500'  onClick={handleGetExportDataLastMonth}>Last Month</button>
                </div>
                <button onClick={handleGetExportData} disabled={loading} className='ActionBtn bg-blue-500'>
                    {loading ? "Loading..." : "Export Data"}
                </button>
            </div>

            <div className='flex flex-col items-start justify-start w-screen h-full p-2 pb-28  overflow-y-auto'>

                <div className='w-full h-full overflow-y-scroll'>

                    {/* {exportData && (
                        <div className='m-0 p-0'>
                            <h3>Export Data:</h3>
                            <pre>{JSON.stringify(exportData, null, 2)}</pre>
                        </div>
                    )} */}

                    <h3 className="text-lg font-bold mb-4">Export Data - {formatDateHumanReadable(startDatetime)} - {formatDateHumanReadable(endDatetime)}</h3>

                    <button
                        onClick={handlePrint}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        Generate PDF
                    </button>
                    <button
                        onClick={handleGenerateCSV}
                        className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                    >
                        Generate CSV
                    </button>

                    <div ref={tableRef} className="mt-4 overflow-x-auto border rounded-lg">
                        <div className="min-w-full bg-gray-100">
                            <div className="grid grid-cols-8 font-semibold bg-gray-200 p-2 border-b data-export-row">
                                <div>Order ID</div>
                                <div>Order Date</div>
                                <div>Status</div>
                                <div>Item Name</div>
                                <div>Price</div>
                                <div>Qty</div>
                                <div>Total Amount</div>
                                <div>Add-on Total</div>
                            </div>
                            {exportData !== null && exportData.map((item: any, index: number) => (
                                <div key={index} className="grid grid-cols-8 gap-1 p-1 border-b data-export-row">
                                    <div>{item.orderId}</div>
                                    <div className='break-words'>{new Date(item.orderDate).toLocaleDateString()}</div>
                                    <div>{item.status}</div>
                                    <div>{item.itemName}</div>
                                    <div>{settings?.currencyName}{item.price}</div>
                                    <div>{item.quantity}</div>
                                    <div>{settings?.currencyName}{item.totalAmount.toFixed(2)}</div>
                                    <div>{settings?.currencyName}{item.addonTotal}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={handlePrint}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        Generate PDF
                    </button>

                    <button
                        onClick={handleGenerateCSV}
                        className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                    >
                        Generate CSV
                    </button>

                </div>
            </div >
        </div>

    );
};

export default ExportData;
